import { createContext, useCallback, useEffect, useReducer } from "react";
import authReducer from "./AuthReducer";
import { getAppConfig, getTenant, getToken, getUser } from "./auth-storage";
import {
  getTenantConfig,
  subscribeToModuleDisabled,
} from "../../services/api";
import { isEmployee, isTenantAdmin } from "../../helpers/auth-role";
import {
  getTenantModules,
  isTenantModuleEnabled,
} from "../../helpers/tenant-modules";

const AuthContext = createContext();

const getTenantCode = (tenant) => tenant?.code ?? null;

const defaultDocumentBranding = {
  title: document.title,
  icons: Array.from(
    document.querySelectorAll(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
    )
  ).map((icon) => ({
    icon,
    href: icon.getAttribute("href"),
  })),
};

const applyDocumentBranding = (branding) => {
  document.title = branding?.appName || defaultDocumentBranding.title;

  defaultDocumentBranding.icons.forEach(({ icon, href }) => {
    if (branding?.logoUrl) {
      icon.setAttribute("href", branding.logoUrl);
      return;
    }

    if (href === null) {
      icon.removeAttribute("href");
      return;
    }

    icon.setAttribute("href", href);
  });
};

const applyBranding = (branding) => {
  applyDocumentBranding(branding);

  const root = document.documentElement;

  if (branding?.primaryColor) {
    root.style.setProperty("--color-primary", branding.primaryColor);
    root.style.setProperty("--color-primary-light", branding.primaryColor);
  }

  if (branding?.secondaryColor) {
    root.style.setProperty("--color-primary-dark", branding.secondaryColor);
  }
};

export const AuthProvider = ({ children }) => {
  const token = getToken();
  const user = getUser();
  const tenant = getTenant();
  const appConfig = getAppConfig(getTenantCode(tenant));

  const initialState = {
    token: token ?? null,
    user: user ?? null,
    tenant: tenant ?? null,
    appConfig: appConfig ?? null,
    configStatus: token ? "idle" : "ready",
    configError: null,
    moduleNotice: null,
  };

  const [state, dispatch] = useReducer(authReducer, initialState);

  const tenantCode = getTenantCode(state.tenant);

  const refreshAppConfig = useCallback(
    async ({ initial = false } = {}) => {
      if (!state.token || !tenantCode) {
        throw new Error("Tenant information is missing from your session.");
      }

      if (initial) {
        dispatch({ type: "CONFIG_LOADING" });
      }

      try {
        const nextAppConfig = await getTenantConfig();
        const configTenantCode = getTenantCode(nextAppConfig?.tenant);

        const sessionIsCurrent =
          getToken() === state.token &&
          getTenantCode(getTenant()) === tenantCode;
        if (!sessionIsCurrent) {
          return null;
        }

        if (configTenantCode !== tenantCode) {
          throw new Error("Tenant config does not match your session.");
        }

        applyBranding(nextAppConfig?.branding);
        dispatch({
          type: "CONFIG_SUCCESS",
          payload: { appConfig: nextAppConfig, tenantCode },
        });
        return nextAppConfig;
      } catch (err) {
        const sessionIsCurrent =
          getToken() === state.token &&
          getTenantCode(getTenant()) === tenantCode;
        if (initial && sessionIsCurrent) {
          dispatch({
            type: "CONFIG_FAILURE",
            payload: {
              message: err.message,
              clearSession: true,
            },
          });
        }
        throw err;
      }
    },
    [state.token, tenantCode]
  );

  useEffect(() => {
    if (!state.token) {
      applyDocumentBranding();
      return;
    }

    if (!tenantCode) {
      dispatch({
        type: "CONFIG_FAILURE",
        payload: {
          message: "Tenant information is missing from your session.",
          clearSession: true,
        },
      });
      return;
    }

    refreshAppConfig({ initial: true }).catch(() => {});
  }, [refreshAppConfig, state.token, tenantCode]);

  useEffect(() => {
    if (!state.token || !tenantCode) {
      return undefined;
    }

    return subscribeToModuleDisabled((moduleKey, message) => {
      dispatch({
        type: "MODULE_DISABLED",
        payload: { moduleKey, tenantCode, message },
      });
      refreshAppConfig().catch(() => {});
    });
  }, [refreshAppConfig, state.token, tenantCode]);

  const saveUserLogin = useCallback((token, user, tenant) => {
    dispatch({
      type: "USER_LOGIN",
      payload: {
        token,
        user,
        tenant,
      },
    });
  }, []);

  const logoutUser = useCallback(() => {
    dispatch({
      type: "USER_LOGOUT",
    });
  }, []);

  const clearModuleNotice = useCallback(() => {
    dispatch({ type: "CLEAR_MODULE_NOTICE" });
  }, []);

  const modules = getTenantModules(state.appConfig);
  const isModuleEnabled = useCallback(
    (moduleKey) => isTenantModuleEnabled(modules, moduleKey),
    [modules]
  );

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        user: state.user,
        tenant: state.tenant,
        appConfig: state.appConfig,
        modules,
        configStatus: state.configStatus,
        configError: state.configError,
        moduleNotice: state.moduleNotice,
        isTenantAdmin: isTenantAdmin(state.token),
        isEmployee: isEmployee(state.token),
        isModuleEnabled,
        refreshAppConfig,
        saveUserLogin,
        logoutUser,
        clearModuleNotice,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
